import React, { useState, useEffect } from 'react';
import { Search, X, Lock } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const AttributeSetSelector = ({ value, onChange, entityType, disabled }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchSets = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/settings/attribute-sets');
        const data = await res.json();
        if (data.status === 'success' && data.data) {
          const validTypes = Array.isArray(entityType) ? entityType : [entityType];
          const filtered = data.data.filter(set => validTypes.includes(set.entity_type));
          setOptions(filtered);
        } else {
          setOptions([
            { set_id: 1, set_name: t('eav.default_set'), set_code: 'default', entity_type: entityType }
          ]);
        }
      } catch (err) {
        setOptions([
          { set_id: 1, set_name: t('eav.default_set'), set_code: 'default', entity_type: entityType }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchSets();
  }, [entityType]);

  // When disabled, show a read-only display
  if (disabled) {
    const selected = options.find(o => o.set_id === value);
    return (
      <div className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800/50 flex items-center gap-2 text-slate-500 dark:text-slate-400 cursor-not-allowed">
        <Lock size={14} className="text-slate-400" />
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {selected ? selected.set_name : (loading ? t('common.loading') : t('eav.select_attribute_set'))}
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer bg-white dark:bg-slate-800 flex justify-between items-center hover:ring-2 hover:ring-blue-500 transition-all"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-slate-700 dark:text-slate-300">
          {options.find(o => o.set_id === value)?.set_name || t('eav.select_attribute_set')}
        </span>
        <Search size={16} className="text-slate-400 dark:text-slate-500" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          <div className="p-2 border-b sticky top-0 bg-white dark:bg-slate-800 dark:border-slate-700">
            <input 
              autoFocus
              className="w-full p-2 text-sm border border-slate-200 dark:border-slate-600 rounded outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              placeholder={t('eav.search_attribute_sets')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          {(() => {
            const filteredOptions = options.filter(o => !query || o.set_name.includes(query) || o.set_code.includes(query));
            return loading ? (
              <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div 
                  key={opt.set_id}
                  className="px-4 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer transition-colors flex justify-between"
                  onClick={() => {
                    onChange(opt.set_id);
                    setIsOpen(false);
                  }}
                >
                  <span className="font-medium dark:text-white">{opt.set_name}</span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">{opt.set_code}</span>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">{t('eav.no_matching_items')}</div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default AttributeSetSelector;
